// championData from https://ddragon.leagueoflegends.com/cdn/14.2.1/data/en_US/champion.json
import championsData from "./champions/championData.json";

export const appVersion = "1.5.0"; // Update game version
export const patchVersionFallback = "16.5";
export const patchVersionCDNFallback = "16.5.1";

// Dynamic patch version — fetched from Riot API at app startup
// eslint-disable-next-line import/no-mutable-exports
export let latestPatchVersion = patchVersionFallback;
// eslint-disable-next-line import/no-mutable-exports
export let latestPatchVersionCDN = patchVersionCDNFallback;

export const fetchLatestPatch = async () => {
  try {
    const res = await fetch(
      "https://ddragon.leagueoflegends.com/api/versions.json",
    );
    const versions = await res.json();
    if (versions && versions.length > 0) {
      [latestPatchVersionCDN] = versions; // e.g. "16.6.1"
      const parts = latestPatchVersionCDN.split(".");
      latestPatchVersion = `${parts[0]}.${parts[1]}`; // e.g. "16.6"
    }
  } catch (e) {
    // Fallback to hardcoded values silently
  }
};

// Keep backward compat for CDN image URLs (they use the fallback since images are on GCS anyway)
const patchVersionCDN = patchVersionCDNFallback;
export const defaultStartingElo = 1000;

const imageMode = "google-storage";

function importAll(r) {
  return r.keys().reduce(
    (acc, item) => ({
      ...acc,
      [item.replace("./", "")]: r(item),
    }),
    {},
  );
}

const localIcons = importAll(
  require.context(
    "../styles/images/champions/icons",
    false,
    /\.(png|jpe?g|svg)$/,
  ),
);
const localSplashes = importAll(
  require.context(
    "../styles/images/champions/splash",
    false,
    /\.(png|jpe?g|svg)$/,
  ),
);

// Google Storage URLs
const GSSplashUrl = (championId) =>
  `https://storage.googleapis.com/drafter-champion-images/champions/splash/${championId}.jpg`;

const GSIconUrl = (championId) =>
  `https://storage.googleapis.com/drafter-champion-images/champions/icons/${championId}.png`;

// Original files, the APIs are not always reliable.
const originalIconUrl = (championKey) =>
  `https://prod.api.assets.riotgames.com/public/v1/asset/lol/${patchVersionCDN}/CHAMPION/${championKey}/ICON?width=128&height=128&auto=png`;

const originalSplashUrl = (championId) =>
  `https://cdn.communitydragon.org/${patchVersionCDN}/champion/${championId}/splash-art/centered`;

const getIconAndSplah = (champion) => {
  if (imageMode === "google-storage") {
    return {
      icon: GSIconUrl(champion.id),
      splash: GSSplashUrl(champion.id),
    };
  }
  if (imageMode === "original") {
    return {
      icon: originalIconUrl(champion.key),
      splash: originalSplashUrl(champion.id),
    };
  }
  return {
    icon: localIcons[`${champion.id}.png`],
    splash: localSplashes[`${champion.id}.jpg`],
  };
};

// TODO - move this function to another file to avoid dependency cycle
// and add better types.
export const fetchChampions = () => {
  const championsArray = Object.keys(championsData).map((key) => {
    const champion = championsData[key];
    return {
      ...champion,
      ...getIconAndSplah(champion),
      // We use gs images for interactions because the RIOT API doesn't allow CORS
      fallBackIcon: GSIconUrl(champion.id),
    };
  });

  const championsMap = new Map(
    championsArray.map((champion) => [champion.id, champion]),
  );

  // eslint-disable-next-line fp/no-mutating-methods
  const sortedNames = [...championsMap.keys()].slice().sort((a, b) => {
    if (a === "NoBan") return -1;
    if (b === "NoBan") return 1;
    return a.localeCompare(b);
  });

  const sortedChampionsMap = new Map(
    sortedNames.map((id) => [id, championsMap.get(id)]),
  );

  return sortedChampionsMap;
};

export const sortedChampionsMap = fetchChampions();

export const getChampionById = (id) => sortedChampionsMap.get(id);

export const standardTurns = {
  0: { type: "ban", nbAction: 1, side: 0 },
  1: { type: "ban", nbAction: 1, side: 1 },
  2: { type: "ban", nbAction: 1, side: 0 },
  3: { type: "ban", nbAction: 1, side: 1 },
  4: { type: "ban", nbAction: 1, side: 0 },
  5: { type: "ban", nbAction: 1, side: 1 },
  6: { type: "pick", nbAction: 1, side: 0 },
  7: { type: "pick", nbAction: 2, side: 1 },
  8: { type: "pick", nbAction: 2, side: 0 },
  9: { type: "pick", nbAction: 1, side: 1 },
  10: { type: "ban", nbAction: 1, side: 1 },
  11: { type: "ban", nbAction: 1, side: 0 },
  12: { type: "ban", nbAction: 1, side: 1 },
  13: { type: "ban", nbAction: 1, side: 0 },
  14: { type: "pick", nbAction: 1, side: 1 },
  15: { type: "pick", nbAction: 2, side: 0 },
  16: { type: "pick", nbAction: 1, side: 1 },
};

export const testTurns = {
  0: { type: "pick", nbAction: 5, side: 0 },
  1: { type: "pick", nbAction: 5, side: 1 },
};

const useTestTurns = !!process.env.REACT_APP_TEST_TURNS;
if (useTestTurns) {
  console.warn("Using test turns");
}
export const turns = useTestTurns ? testTurns : standardTurns;

export const soloTurns = {
  0: { type: "pick", nbAction: 5, side: 0 },
  1: { type: "pick", nbAction: 5, side: 1 },
};

// only keep the pick/ban that are authrized for the current turn
export const validatedPickBanArrayByTurn = ({
  pickBanArray,
  currentTurn,
  side,
}) => {
  const type = turns[currentTurn]?.type;
  const totalActionByType = Object.values(turns)
    .slice(0, currentTurn + 1)
    .reduce(
      (acc, turn) =>
        turn.type === type && turn.side === side ? acc + turn.nbAction : acc,
      0,
    );
  return (pickBanArray || []).slice(0, totalActionByType);
};
